import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Sparkles, Flame } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

/**
 * GuestActionBanner — Renders a top CTA banner (no blur) for unauthenticated
 * visitors. Same UX intent as Analyse IA / Calculateur: the underlying page
 * stays fully visible but a persistent banner invites the guest to create an
 * account to actually use the features.
 *
 * If the user is authenticated (or auth is still loading), nothing renders.
 */
export default function GuestActionBanner({
  title = 'Aperçu invité — créez un compte pour utiliser cette fonctionnalité',
  description = "Vous voyez l'interface complète. Les actions (sauvegarde, ajout, scan) sont réservées aux membres. Inscription gratuite en 30 secondes.",
  primaryLabel = 'Créer un compte gratuit',
  primaryTo = '/register',
  secondaryLabel = 'Se connecter',
  secondaryTo = '/login',
  showFoundersHint = true,
  className = '',
  'data-testid': dataTestId = 'guest-action-banner',
}) {
  const { user, loading } = useAuth();
  if (loading || user) return null;

  return (
    <Card
      className={`bg-primary/5 border-primary/30 shadow-md ${className}`}
      data-testid={dataTestId}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="shrink-0 w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
              <Lock className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-bold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-400 shrink-0" />
                <span className="truncate">{title}</span>
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {description}
              </p>
              {showFoundersHint && (
                <Link
                  to="/subscribe"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 transition-colors"
                  data-testid={`${dataTestId}-founders-hint`}
                >
                  <Flame className="w-3.5 h-3.5" />
                  Offre Fondateurs — 100 places à saisir
                </Link>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0">
            <Link to={primaryTo}>
              <Button
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 font-bold"
                data-testid={`${dataTestId}-primary-btn`}
              >
                {primaryLabel}
              </Button>
            </Link>
            <Link to={secondaryTo}>
              <Button
                variant="outline"
                className="w-full sm:w-auto border-white/20"
                data-testid={`${dataTestId}-secondary-btn`}
              >
                {secondaryLabel}
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
